import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, LogOut, FileText, Pencil, Trash2 } from "lucide-react";

export default function AdminResources() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/admin/resources"),
        api.get("/resource-categories"),
      ]);
      setResources(r.data);
      setCategories(c.data);
    } catch {
      toast.error("Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/resources/${id}`);
      toast.success("Resource deleted");
      load();
    } catch {
      toast.error("Failed to delete resource");
    }
  };

  const grouped = categories.map((c) => ({
    ...c,
    items: resources.filter((r) => r.category === c.slug),
  }));

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-resources">
      <header className="border-b border-line bg-bone-surface">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo">
            <span className="font-serif text-2xl text-forest">Shelton Law</span>
            <span className="text-xs uppercase tracking-[0.2em] text-gold-deep border-l border-line pl-3">Admin</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-xs uppercase tracking-[0.2em] font-semibold">
            <Link to="/admin" className="text-ink-soft hover:text-forest" data-testid="admin-nav-posts">Posts</Link>
            <Link to="/admin/resources" className="text-forest border-b border-gold pb-1" data-testid="admin-nav-resources">Resources</Link>
          </nav>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/resources" className="text-ink-soft hover:text-forest" target="_blank" data-testid="admin-view-site">View public</Link>
            <span className="text-ink-soft hidden md:inline">{user?.email}</span>
            <button
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="flex items-center gap-2 text-ink hover:text-forest"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="overline mb-2">Content Management</p>
            <h1 className="font-serif text-4xl text-forest tracking-tight">Resources (PDFs)</h1>
            <p className="text-ink-soft text-sm mt-2">{resources.length} {resources.length === 1 ? "document" : "documents"} · {categories.length} categories</p>
          </div>
          <Button
            onClick={() => navigate("/admin/resources/new")}
            className="bg-forest text-bone hover:bg-forest-light rounded-none px-6 py-5"
            data-testid="admin-new-resource-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New resource
          </Button>
        </div>

        {loading && <p className="text-ink-soft" data-testid="admin-resources-loading">Loading…</p>}

        {!loading && grouped.map((cat) => (
          <section key={cat.slug} className="mb-12" data-testid={`admin-cat-${cat.slug}`}>
            <div className="flex items-baseline justify-between mb-4 border-b border-line pb-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-gold-deep font-semibold">{cat.slug}</p>
                <h2 className="font-serif text-2xl text-forest mt-1">{cat.name}</h2>
              </div>
              <span className="text-ink-soft text-sm">{cat.items.length} {cat.items.length === 1 ? "item" : "items"}</span>
            </div>

            {cat.items.length === 0 ? (
              <div className="border border-dashed border-line p-8 text-center">
                <FileText className="w-8 h-8 mx-auto text-ink-soft mb-2" strokeWidth={1.5} />
                <p className="text-ink-soft text-sm">No resources in this category yet.</p>
              </div>
            ) : (
              <div className="border border-line divide-y divide-line">
                {cat.items.map((r) => (
                  <div
                    key={r.id}
                    className="p-5 flex flex-col md:flex-row md:items-center gap-4 bg-bone hover:bg-bone-alt transition-colors"
                    data-testid={`admin-resource-row-${r.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1.5">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-gold-deep font-semibold">{r.format}</span>
                        <span className="text-[10px] text-ink-soft">order: {r.order}</span>
                      </div>
                      <h3 className="font-serif text-lg text-forest leading-tight">{r.title}</h3>
                      <p className="text-xs text-ink-soft mt-1 truncate">{r.description}</p>
                      <a
                        href={r.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-ink-soft hover:text-forest break-all inline-block mt-1"
                      >
                        {r.href}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/resources/${r.id}/edit`}
                        className="p-2 text-ink-soft hover:text-forest"
                        data-testid={`admin-edit-resource-${r.id}`}
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(r.id, r.title)}
                        className="p-2 text-ink-soft hover:text-destructive"
                        data-testid={`admin-delete-resource-${r.id}`}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </main>
    </div>
  );
}
